import * as React from "react";
import { Alert, MenuItem, Stack, TextField } from "@mui/material";
import CclDrawer from "../../common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../common/cclButtons/cclButton";
import CclLoadingButton from "../../common/cclButtons/cclLoadingButton";
import { useUpdateUserNameMutation } from "../../../services/cclTokenedEnterpriseParticipantApi";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import {
    useGetLanguagesQuery,
    useGetPreferredLanguageQuery,
    useSetPreferredLanguageMutation,
} from "../../../services/cclTokenedEnterpriseUserApi";

export type EditUserDrawerProps = {
    title: string;
    open: boolean;
    imKey: number;
    email: string;
    firstName: string;
    lastName: string;
    onClose: (success: boolean, firstName?: string, lastName?: string) => void;
};

const NO_PREFERRED_LANGUAGE = "No Preferred Language Set";

const EditUserDrawer: React.FC<EditUserDrawerProps> = (props) => {
    const [firstName, setFirstName] = React.useState<string>(props.firstName);
    const [lastName, setLastName] = React.useState<string>(props.lastName);
    const [languageKey, setLanguageKey] = React.useState<number | null>(null);
    const [initialLanguageKey, setInitialLanguageKey] = React.useState<number | null>(null);
    const [saveError, setSaveError] = React.useState<string>("");
    const appliedInitialLanguageRef = React.useRef<boolean>(false);

    const [updateUserName, { isLoading: isSavingName }] = useUpdateUserNameMutation();
    const [setPreferredLanguage, { isLoading: isSavingLanguage }] =
        useSetPreferredLanguageMutation();
    const { data: languages } = useGetLanguagesQuery(undefined, { skip: !props.open });
    const { data: preferredLanguageData, isFetching: preferredLanguageFetching } =
        useGetPreferredLanguageQuery(props.imKey, {
            skip: !props.open || !props.imKey,
        });
    const claimsService = React.useMemo(() => new UserClaimsService(), []);

    React.useEffect(() => {
        if (props.open) {
            setFirstName(props.firstName);
            setLastName(props.lastName);
            setSaveError("");
            setLanguageKey(null);
            setInitialLanguageKey(null);
            appliedInitialLanguageRef.current = false;
        }
    }, [props.open, props.firstName, props.lastName]);

    React.useEffect(() => {
        if (props.open && !preferredLanguageFetching && !appliedInitialLanguageRef.current) {
            const current = preferredLanguageData?.languageKey ?? null;
            setLanguageKey(current);
            setInitialLanguageKey(current);
            appliedInitialLanguageRef.current = true;
        }
    }, [props.open, preferredLanguageData, preferredLanguageFetching]);

    const nameChanged = firstName !== props.firstName || lastName !== props.lastName;
    const languageChanged = languageKey !== initialLanguageKey;

    const handleSave = async () => {
        setSaveError("");

        if (nameChanged) {
            try {
                await updateUserName({
                    esiKey: -1,
                    imKey: props.imKey,
                    firstName: firstName,
                    lastName: lastName,
                }).unwrap();

                if (props.imKey === Number(claimsService.GetCurrentUserId())) {
                    claimsService.StoreCurrentUserName(firstName, lastName);
                }
            } catch {
                props.onClose(false);
                return;
            }
        }

        if (languageChanged && languageKey != null) {
            try {
                await setPreferredLanguage({ imkey: props.imKey, languageKey }).unwrap();
            } catch {
                setSaveError(
                    nameChanged
                        ? "Your name was saved, but the preferred language failed to save. Please try again."
                        : "Failed to save preferred language. Please try again.",
                );
                return;
            }
        }

        props.onClose(true, firstName, lastName);
    };

    return (
        <CclDrawer title={props.title} open={props.open} onClose={() => props.onClose(false)}>
            <Stack spacing={4} sx={{ p: 2 }}>
                {saveError && <Alert severity="error">{saveError}</Alert>}
                <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    size="small"
                    fullWidth
                />
                <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    size="small"
                    fullWidth
                />
                <TextField
                    select
                    label="Preferred Language"
                    value={languageKey ?? ""}
                    onChange={(e) => setLanguageKey(Number(e.target.value))}
                    size="small"
                    fullWidth
                    disabled={preferredLanguageFetching}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => {
                            if (value === "" || value == null) return NO_PREFERRED_LANGUAGE;
                            const found = languages?.find((l) => l.languageKey === value);
                            return found?.sLanguage ?? "";
                        },
                    }}
                >
                    {languages?.map((l) => (
                        <MenuItem key={l.languageKey} value={l.languageKey}>
                            {l.sLanguage}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            <CclDrawerActionBar>
                <CclLoadingButton
                    onClick={handleSave}
                    mode="primary"
                    loading={isSavingName || isSavingLanguage}
                    disabled={
                        !firstName.trim() ||
                        !lastName.trim() ||
                        (!nameChanged && !languageChanged)
                    }
                >
                    Save
                </CclLoadingButton>
                <CclButton onClick={() => props.onClose(false)} mode="secondary">
                    Cancel
                </CclButton>
            </CclDrawerActionBar>
        </CclDrawer>
    );
};

export default EditUserDrawer;
